import { Button } from 'antd';
import { Thumbnail } from 'api';
import { ExpandableParagraph } from 'components/expandable-paragraph/expandable-paragraph';
import SummerwoodSVG from 'components/summerwoodsvg/summerwood-svg';
import { motion } from 'framer-motion';
import { UI } from 'helpers/constants';
import Typography from 'helpers/fonts';
import { getImageUrl } from 'helpers/selectExistingImage';
import { useHover } from 'helpers/useHover';
import { formatMoney } from 'helpers/util';
import React, { RefObject } from 'react';
import { addFeature, removeFeature } from 'viewer/viewerActions';
import { OptionInstance } from '@swd_admin/cdc-viewer';
import { FeatureInstance } from './tabpane-features';

export interface TabPaneCardsProps {
  features: Array<FeatureInstance>;
  optionInstance: OptionInstance;
}

interface TabPaneCardProps {
  onClick: () => void;
  onRemoveClick: () => void;
  title: string;
  description: string;
  price: number;
  img: string;
  selected: boolean;
  removable: boolean;
}

const TabPaneCard: React.FunctionComponent<TabPaneCardProps> = ({
  title,
  description,
  price,
  onClick,
  onRemoveClick,
  img,
  selected,
  removable,
}: TabPaneCardProps) => {
  const [hoverRef, isHovered] = useHover();

  return (
    <div
      ref={hoverRef as RefObject<HTMLDivElement>}
      style={{
        maxHeight: '220px',
        height: '100%',
        background: Theme['@white'],
        borderRadius: '5px',
        display: 'flex',
        alignContent: 'center',
        padding: '8px',

        border: '1px solid',
        borderColor: isHovered || selected ? Theme['@primary-color'] : 'transparent',
      }}
    >
      <div
        style={{
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
          display: 'flex',
          width: '25%',
          borderRight: `1px solid ${Theme['@grey-normal']}`,
        }}
      >
        <img
          style={{
            maxWidth: '100%',
            padding: '0 8px',
          }}
          alt=""
          src={img}
        />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateRows: 'auto 1fr 16px',
          gridTemplateColumns: '1fr',
          width: '75%',
          flexDirection: 'column',
          height: '100%',
          padding: '0px 8px 0 16px',
          justifyContent: 'space-between',
          overflow: 'hidden',
        }}
      >
        <Typography
          styleid={10}
          style={{
            fontWeight: 500,
            maxWidth: '100%',
          }}
        >
          {title}
        </Typography>
        {<ExpandableParagraph text={description} />}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography styleid={9} style={{ color: Theme['@black'] }}>
            {`${formatMoney(price)}`}
          </Typography>
          {selected && removable ? (
            <Button
              type="link"
              onClick={onRemoveClick}
              style={{ padding: 0, height: 22 }}
            >
              <SummerwoodSVG type="minusButton" style={{ height: 18 }} />
            </Button>
          ) : (
            <Button
              type="link"
              onClick={onClick}
              style={{ padding: 0, height: 22 }}
            >
              <SummerwoodSVG type="plusButton" style={{ height: 18 }} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const TabPaneCards: React.FunctionComponent<TabPaneCardsProps> = ({
  features,
  optionInstance,
}: TabPaneCardsProps) => {
  const itemVariants = {
    show: {
      y: 0,
      opacity: 1,
      transition: {
        y: {
          stiffness: 1000,
          velocity: -100,
          ease: UI.EASE_QUINT,
          duration: 0.4,
        },
      },
    },
    hidden: {
      y: 500,
      opacity: 0,
      transition: {
        y: { stiffness: 1000 },
      },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.07,
      },
    },
  };

  const optionCards = features.map((f) => (
    <motion.span variants={itemVariants} key={f.id} style={{ margin: '6px 0' }}>
      <TabPaneCard
        key={f.id}
        description={f.description}
        price={f.pricePerUnit}
        title={f.title}
        img={getImageUrl(f.thumbnail as Thumbnail)}
        onClick={() => addFeature(f, optionInstance)}
        onRemoveClick={() => f.instanceId && removeFeature(f.instanceId)}
        selected={!!f.instanceId}
        removable={f.optionTypeId !== 'door_features'}
      />
    </motion.span>
  ));

  return (
    <motion.div
      style={{
        display: 'grid',
        gridAutoColumns: '1fr',
        gridAutoRows: 'auto',
      }}
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      {optionCards}
    </motion.div>
  );
};

export default TabPaneCards;
